import { memo, useLayoutEffect, useMemo, useRef, type ReactNode, type RefObject } from "react";
import { useSelector } from "react-redux";

import { useAuiState } from "../aui";
import { useMirrorBox } from "../carets";
import type { AgentState, OutgoingFile } from "../store/agentSlice";
import { canPreview } from "./FilePreviewDialog";
import { MENTION_CHIP, MentionEye } from "./MentionChip";
import { findMentions, resolveMention } from "./mention";

type State = { agent: AgentState };

const NO_FILES: OutgoingFile[] = [];
// Keeps a trailing newline's empty line, as the textarea shows it.
const LINE_END = String.fromCharCode(0x200b);

/**
 * The mentions of the draft's files, drawn as chips over the composer's textarea: the textarea
 * keeps the text, this layer wraps the same text the same way (useMirrorBox), invisible except
 * for each mention's highlight and the eye over its "@". Mount it right after the input, inside
 * the same `relative` parent; the caret overlay goes after it, on top.
 */
export const MentionOverlay = memo(function MentionOverlay({
  input,
  threadId,
  pending,
  onPreview,
}: {
  input: RefObject<HTMLTextAreaElement | null>;
  threadId: number | null;
  /** Names of documents still being linked: shown as mentions, with a spinner. */
  pending: readonly string[];
  onPreview: (file: OutgoingFile) => void;
}) {
  const mirror = useRef<HTMLDivElement>(null);
  const text = useAuiState((state) => state.composer.text);
  const files = useSelector((state: State) =>
    threadId == null ? NO_FILES : (state.agent.threads[threadId]?.composer.files ?? NO_FILES)
  );
  useMirrorBox(input, mirror);

  // A remote edit or a pick scrolls the textarea without a scroll event of ours.
  useLayoutEffect(() => {
    if (mirror.current && input.current) mirror.current.scrollTop = input.current.scrollTop;
  }, [input, text]);

  const nodes = useMemo(() => {
    const tokens = findMentions(text, [...files.map((file) => file.name), ...pending]);
    if (!tokens.length) return null;
    const out: ReactNode[] = [];
    let from = 0;
    for (const token of tokens) {
      const file = resolveMention(files, token.name);
      out.push(
        text.slice(from, token.start),
        <span key={token.start} className={MENTION_CHIP}>
          <span className="relative">
            @
            <MentionEye
              name={token.name}
              over
              pending={!file}
              onPreview={file && canPreview(file.contentType) ? () => onPreview(file) : undefined}
            />
          </span>
          {text.slice(token.start + 1, token.end)}
        </span>
      );
      from = token.end;
    }
    out.push(text.slice(from), LINE_END);
    return out;
  }, [text, files, pending, onPreview]);

  return (
    <div
      ref={mirror}
      aria-hidden
      className="pointer-events-none absolute select-none overflow-hidden text-transparent"
    >
      {nodes}
    </div>
  );
});
